import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import API, { authFetch } from './api';
import useScoringStatus from './useScoringStatus';
import { ScoringChip, ScoringBanner } from './ScoringStatus';

const READINESS_DOT_BG = {
  'Hot Lead':    'bg-orange-500',
  'Warm Lead':   'bg-yellow-500',
  'Active':      'bg-[#00D4FF]',
  'Early Stage': 'bg-gray-500',
  'Cold':        'bg-slate-600',
};

const READINESS_BADGE = {
  'Hot Lead':    'bg-orange-500/20 text-orange-300 border-orange-500/40',
  'Warm Lead':   'bg-yellow-500/20 text-yellow-300 border-yellow-500/40',
  'Active':      'bg-[#00D4FF]/20 text-[#00D4FF] border-[#00D4FF]/40',
  'Early Stage': 'bg-gray-500/20 text-gray-300 border-gray-500/40',
  'Cold':        'bg-slate-600/30 text-slate-300 border-slate-500/40',
};

const READINESS_RING_COLOR = {
  'Hot Lead':    '#F97316',
  'Warm Lead':   '#EAB308',
  'Active':      '#00D4FF',
  'Early Stage': '#6B7280',
  'Cold':        '#475569',
};

const TIMELINE_LABELS = {
  '30_days': '30 days', '60_days': '60 days', '90_days': '90 days',
  '6_months': '6 months', 'exploring': 'Exploring', 'ready_now': 'Ready now',
};
const PREAPPROVAL_LABELS = {
  'approved': 'Approved', 'in_process': 'In Process', 'in_progress': 'In Progress',
  'not_started': 'Not Started', 'unknown': 'Unknown',
};
const MOTIVATION_LABELS = {
  'must_move': 'Must Move', 'want_to_move': 'Want to Move',
  'highly_motivated': 'Highly Motivated', 'motivated': 'Motivated',
  'casually_looking': 'Casual', 'casual': 'Casual',
};

export default function BuyerManagement() {
  const { user } = useAuth();

  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [resetId, setResetId] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ buyer_name: '', buyer_email: '' });
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [latestInvite, setLatestInvite] = useState(null);
  const [invites, setInvites] = useState([]);
  const [copiedCode, setCopiedCode] = useState(null);

  const [viewMode, setViewMode] = useState('default'); // 'default' | 'readiness'
  const [readiness, setReadiness] = useState([]);
  const [showInviteHistory, setShowInviteHistory] = useState(false);

  const emptyForm = { name: '', email: '', phone: '', notes: '', give_login: false, login_password: '', timeline: 'exploring', pre_approval_status: 'unknown', motivation: 'casually_looking' };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { loadBuyers(); loadInvites(); loadReadiness(); }, []);

  // Background scoring state — a newly created/imported buyer shows "Scoring…" until the
  // worker finishes, then the list reloads itself so the fresh matches appear.
  const { status: scoringStatus, buyerState, failedJob, retry: retryScoring } =
    useScoringStatus({ onSettled: () => { loadBuyers(); loadReadiness(); } });

  const loadReadiness = async () => {
    try {
      const res = await authFetch(`${API}/api/readiness/scores`);
      if (res.ok) setReadiness(await res.json());
    } catch (err) {
      console.error('Load readiness error:', err);
    }
  };

  const loadInvites = async () => {
    try {
      const res = await authFetch(`${API}/api/invites`);
      if (res.ok) setInvites(await res.json());
    } catch (err) {
      console.error('Load invites error:', err);
    }
  };

  const handleCreateInvite = async (e) => {
    e.preventDefault();
    setCreatingInvite(true);
    setError('');
    try {
      const res = await authFetch(`${API}/api/invites`, {
        method: 'POST',
        body: JSON.stringify({
          buyer_name: inviteForm.buyer_name.trim() || null,
          buyer_email: inviteForm.buyer_email.trim() || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || 'Failed to generate invite');
      } else {
        setLatestInvite(data);
        loadInvites();
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setCreatingInvite(false);
    }
  };

  const handleCancelInvite = async (id) => {
    if (!window.confirm('Cancel this invite link?')) return;
    try {
      const res = await authFetch(`${API}/api/invites/${id}`, { method: 'DELETE' });
      if (res.ok) loadInvites();
    } catch (err) {
      console.error('Cancel invite error:', err);
    }
  };

  const copyInvite = async (code, url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(c => (c === code ? null : c)), 2000);
    } catch (err) {
      console.error('Clipboard error:', err);
    }
  };

  const loadBuyers = async () => {
    try {
      const res = await authFetch(`${API}/api/buyers`);
      if (res.ok) setBuyers(await res.json());
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    setSuccess('');

    try {
      // Step 1: Create the buyer record
      const buyerRes = await authFetch(`${API}/api/buyers`, {
        method: 'POST',
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          notes: form.notes,
          timeline: form.timeline,
          pre_approval_status: form.pre_approval_status,
          motivation: form.motivation,
        })
      });

      if (!buyerRes.ok) {
        const err = await buyerRes.json();
        setError(err.detail || 'Failed to create buyer');
        setCreating(false);
        return;
      }

      const buyerData = await buyerRes.json();
      let loginMsg = '';

      // Step 2: If login access requested, create user account
      if (form.give_login && form.email && form.login_password) {
        const userRes = await authFetch(`${API}/api/auth/register`, {
          method: 'POST',
          body: JSON.stringify({
            full_name: form.name,
            email: form.email,
            password: form.login_password,
            role: 'buyer',
            brokerage_id: user?.brokerage_id || null,
            agent_id: user?.agent_id || null,
            buyer_id: buyerData.id,
          })
        });

        if (userRes.ok) {
          loginMsg = ' Login access granted.';
        } else {
          const err = await userRes.json();
          loginMsg = ` (Warning: buyer created but login failed — ${err.detail || 'unknown error'})`;
        }
      }

      setSuccess(`"${buyerData.name}" created with ${buyerData.criteria_count || 22} default criteria.${loginMsg}`);
      setForm(emptyForm);
      setShowCreate(false);
      loadBuyers();
      loadReadiness();
    } catch (err) {
      setError('Network error');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (buyerId, name) => {
    if (!window.confirm(`Delete "${name}" and all their criteria, grades, and scores?`)) return;
    try {
      const res = await authFetch(`${API}/api/buyers/${buyerId}`, { method: 'DELETE' });
      if (res.ok) { loadBuyers(); loadReadiness(); }
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const handleResetPassword = async (buyerId) => {
    if (!newPassword || newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    try {
      // Find the user account linked to this buyer
      const res = await authFetch(`${API}/api/auth/reset-buyer-password`, {
        method: 'POST',
        body: JSON.stringify({ buyer_id: buyerId, new_password: newPassword })
      });
      if (res.ok) {
        setSuccess('Password reset successfully');
        setResetId(null);
        setNewPassword('');
      } else {
        const err = await res.json();
        setError(err.detail || 'Failed to reset password');
      }
    } catch (err) {
      setError('Network error');
    }
  };

  const updateUrgency = async (buyerId, field, value) => {
    try {
      await authFetch(`${API}/api/buyers/${buyerId}/urgency`, {
        method: 'PUT',
        body: JSON.stringify({ [field]: value })
      });
      setBuyers(prev => prev.map(b => b.id === buyerId ? { ...b, [field]: value } : b));
    } catch (err) {
      console.error('Update urgency error:', err);
    }
  };

  const filtered = search
    ? buyers.filter(b => b.name?.toLowerCase().includes(search.toLowerCase()) || b.email?.toLowerCase().includes(search.toLowerCase()))
    : buyers;

  const readinessByBuyer = readiness.reduce((acc, r) => { acc[r.buyer_id] = r; return acc; }, {});
  const filteredBuyerIds = new Set(filtered.map(b => b.id));
  const filteredReadiness = readiness.filter(r => filteredBuyerIds.has(r.buyer_id));

  // Role guard: buyers cannot access buyer management
  if (user && user.role === 'buyer') {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-400 text-lg font-semibold mb-2">Access Denied</div>
          <p className="text-gray-400 text-sm">This page is for agents and administrators only.</p>
          <Link to="/" className="text-[#00D4FF] text-sm mt-4 inline-block hover:text-[#00E676]">← Back to Home</Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center">
        <div className="text-[#00D4FF] text-lg">Loading buyers...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0E27] pt-8 pb-12">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 md:gap-0 mb-6">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-white">Buyer Management</h1>
            <p className="text-[#B0BEC5] text-sm mt-1">{buyers.length} buyer{buyers.length !== 1 ? 's' : ''}</p>
          </div>
          <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
            <button
              onClick={() => { setShowInvite(false); setLatestInvite(null); setShowCreate(!showCreate); }}
              className="px-4 py-2 bg-[#00D4FF] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00B8D4] transition-colors w-full md:w-auto"
            >
              {showCreate ? 'Cancel' : '+ Add Buyer'}
            </button>
            <button
              onClick={() => { setShowCreate(false); setShowInvite(!showInvite); setLatestInvite(null); }}
              className="px-4 py-2 bg-[#243B55] text-[#00D4FF] font-semibold rounded-lg hover:bg-[#2d4a6a] border border-[#00D4FF]/30 transition-colors w-full md:w-auto"
            >
              {showInvite ? 'Cancel' : 'Generate Invite Link'}
            </button>
          </div>
        </div>

        <ScoringBanner status={scoringStatus} onRetry={retryScoring} label="clients" />

        {/* Messages */}
        {success && <div className="bg-green-900/50 border border-green-600 text-green-200 px-4 py-3 rounded-lg mb-4 text-sm">{success}</div>}
        {error && <div className="bg-red-900/50 border border-red-700 text-red-300 px-4 py-3 rounded-lg mb-4 text-sm">{error}</div>}

        {/* Create Form */}
        {showCreate && (
          <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6 mb-6">
            <h2 className="text-lg font-semibold text-white mb-1">New Buyer</h2>
            <p className="text-gray-400 text-sm mb-4">Default evaluation criteria will be automatically set up for this buyer.</p>
            <form onSubmit={handleCreate} autoComplete="off" className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-300 mb-1">Name *</label>
                <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                  placeholder="Carlos & Maria Ramirez" required />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Email</label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                  placeholder="carlos@email.com" autoComplete="off" />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Phone</label>
                <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                  placeholder="(321) 555-0142" />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Notes</label>
                <input type="text" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                  placeholder="Budget, preferences, timeline..." />
              </div>

              {/* Urgency & Readiness */}
              <div>
                <label className="block text-sm text-gray-300 mb-1">Timeline</label>
                <select value={form.timeline} onChange={(e) => setForm({ ...form, timeline: e.target.value })}
                  className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#00D4FF]">
                  <option value="30_days">30 days</option>
                  <option value="60_days">60 days</option>
                  <option value="90_days">90 days</option>
                  <option value="6_months">6 months</option>
                  <option value="exploring">Exploring</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Pre-Approval</label>
                <select value={form.pre_approval_status} onChange={(e) => setForm({ ...form, pre_approval_status: e.target.value })}
                  className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#00D4FF]">
                  <option value="approved">Approved</option>
                  <option value="in_process">In Process</option>
                  <option value="not_started">Not Started</option>
                  <option value="unknown">Unknown</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Motivation</label>
                <select value={form.motivation} onChange={(e) => setForm({ ...form, motivation: e.target.value })}
                  className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#00D4FF]">
                  <option value="must_move">Must Move</option>
                  <option value="want_to_move">Want to Move</option>
                  <option value="casually_looking">Casual</option>
                </select>
              </div>

              {/* Login Access Toggle */}
              <div className="md:col-span-2 bg-[#0A0E27] rounded-lg border border-[#546E7A]/30 p-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={form.give_login}
                    onChange={(e) => setForm({ ...form, give_login: e.target.checked })}
                    className="w-5 h-5 rounded border-[#546E7A] text-[#00D4FF] focus:ring-[#00D4FF] bg-[#1A2744]" />
                  <div>
                    <span className="text-white font-medium">Give this buyer login access</span>
                    <p className="text-gray-500 text-xs mt-0.5">Buyer can log in to view their match board, fit scores, and AI reports</p>
                  </div>
                </label>

                {form.give_login && (
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-300 mb-1">Buyer's Login Email *</label>
                      <input type="email" value={form.email}
                        className="w-full px-3 py-2 bg-[#1A2744] border border-[#546E7A]/50 rounded-lg text-gray-400 text-sm cursor-not-allowed"
                        disabled placeholder="Uses the email entered above" />
                      <p className="text-gray-600 text-xs mt-1">Uses the email address above</p>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-300 mb-1">Password *</label>
                      <input type="password" value={form.login_password}
                        onChange={(e) => setForm({ ...form, login_password: e.target.value })}
                        className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                        placeholder="Min 8 characters" required={form.give_login} autoComplete="new-password" />
                    </div>
                  </div>
                )}
              </div>

              <div className="md:col-span-2">
                <button type="submit" disabled={creating}
                  className="px-6 py-2 bg-[#00E676] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00C853] disabled:opacity-50 transition-colors">
                  {creating ? 'Creating...' : 'Create Buyer'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Invite Form */}
        {showInvite && (
          <div className="bg-[#1A2744] rounded-xl border border-[#00D4FF]/30 p-4 md:p-6 mb-6">
            <h2 className="text-lg font-semibold text-white mb-1">Invite a buyer</h2>
            <p className="text-gray-400 text-sm mb-4">
              Generate a link the buyer can use to create their own account. Their criteria and matches will be set up automatically.
            </p>

            {!latestInvite ? (
              <form onSubmit={handleCreateInvite} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Buyer name <span className="text-gray-600">(optional)</span></label>
                  <input type="text" value={inviteForm.buyer_name}
                    onChange={(e) => setInviteForm({ ...inviteForm, buyer_name: e.target.value })}
                    className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                    placeholder="Carlos & Maria Ramirez" />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Buyer email <span className="text-gray-600">(optional)</span></label>
                  <input type="email" value={inviteForm.buyer_email}
                    onChange={(e) => setInviteForm({ ...inviteForm, buyer_email: e.target.value })}
                    className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                    placeholder="carlos@email.com" />
                </div>
                <div className="md:col-span-2">
                  <button type="submit" disabled={creatingInvite}
                    className="px-6 py-2 bg-[#00D4FF] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00B8D4] disabled:opacity-50 transition-colors w-full md:w-auto">
                    {creatingInvite ? 'Generating...' : 'Generate'}
                  </button>
                </div>
              </form>
            ) : (
              <div>
                <p className="text-gray-300 text-sm mb-2">Share this link with the buyer:</p>
                <div className="flex flex-col md:flex-row gap-2 items-stretch md:items-center">
                  <input readOnly value={latestInvite.invite_url}
                    className="flex-1 min-w-0 px-3 py-2 bg-[#0A0E27] border border-[#00D4FF]/50 rounded-lg text-white text-sm font-mono"
                    onFocus={(e) => e.target.select()} />
                  <button onClick={() => copyInvite(latestInvite.invite_code, latestInvite.invite_url)}
                    className="px-4 py-2 bg-[#00E676] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00C853] transition-colors whitespace-nowrap">
                    {copiedCode === latestInvite.invite_code ? 'Copied!' : 'Copy Link'}
                  </button>
                </div>
                <p className="text-gray-500 text-xs mt-2">
                  Expires {new Date(latestInvite.expires_at).toLocaleDateString()}
                </p>
                <button onClick={() => { setLatestInvite(null); setInviteForm({ buyer_name: '', buyer_email: '' }); }}
                  className="mt-3 text-gray-400 hover:text-white text-sm">
                  Generate another
                </button>
              </div>
            )}
          </div>
        )}

        {/* Invite Links List — pending only by default, with optional history toggle */}
        {(() => {
          const pendingInvites = invites.filter(i => i.status === 'pending');
          const historyInvites = invites.filter(i => i.status !== 'pending');
          if (pendingInvites.length === 0) return null;

          const renderRow = (inv) => (
            <div key={inv.id} className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 p-3 bg-[#0A0E27]/50 rounded-lg">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {inv.status === 'pending' && (
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-900/50 text-blue-300 border border-blue-500/30">Pending</span>
                  )}
                  {inv.status === 'used' && (
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-900/50 text-green-300 border border-green-500/30">Used</span>
                  )}
                  {inv.status === 'expired' && (
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-900/40 text-red-300 border border-red-500/30">Expired</span>
                  )}
                  <span className="text-white text-sm font-medium truncate">
                    {inv.signed_up_buyer_name || inv.buyer_name || '(unnamed)'}
                  </span>
                  {inv.buyer_email && (
                    <span className="text-gray-500 text-xs min-w-0 break-all">{inv.buyer_email}</span>
                  )}
                </div>
                <div className="text-gray-500 text-xs mt-1 font-mono truncate">{inv.invite_url}</div>
                <div className="text-gray-600 text-xs mt-0.5">
                  Created {new Date(inv.created_at).toLocaleDateString()}
                  {inv.status === 'used' && inv.used_at && (
                    <> · Used {new Date(inv.used_at).toLocaleDateString()}</>
                  )}
                  {inv.status === 'pending' && (
                    <> · Expires {new Date(inv.expires_at).toLocaleDateString()}</>
                  )}
                </div>
              </div>
              <div className="flex gap-2 shrink-0 flex-wrap">
                {inv.status === 'pending' && (
                  <>
                    <button onClick={() => copyInvite(inv.invite_code, inv.invite_url)}
                      className="px-3 py-1.5 bg-[#243B55] text-[#00D4FF] text-xs font-medium rounded-lg hover:bg-[#2d4a6a] transition-colors whitespace-nowrap">
                      {copiedCode === inv.invite_code ? 'Copied!' : 'Copy'}
                    </button>
                    <button onClick={() => handleCancelInvite(inv.id)}
                      className="px-3 py-1.5 text-red-400 hover:text-red-300 text-xs font-medium rounded-lg hover:bg-red-900/20 transition-colors">
                      Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
          );

          return (
            <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6 mb-6">
              <h2 className="text-lg font-semibold text-white mb-3">Invite Links ({pendingInvites.length})</h2>
              <div className="space-y-2">
                {pendingInvites.map(renderRow)}
              </div>

              {historyInvites.length > 0 && (
                <>
                  <button
                    onClick={() => setShowInviteHistory(v => !v)}
                    className="text-[#00D4FF] text-xs hover:text-[#00E676] mt-3 transition-colors"
                  >
                    {showInviteHistory ? 'Hide history' : `Show history (${historyInvites.length})`}
                  </button>
                  {showInviteHistory && (
                    <div className="space-y-2 mt-3 pt-3 border-t border-[#546E7A]/20">
                      {historyInvites.map(renderRow)}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })()}

        {/* Search + View Toggle */}
        {buyers.length > 0 && (
          <div className="flex flex-col md:flex-row gap-3 mb-6 md:items-center">
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              className="flex-1 px-4 py-2 bg-[#1A2744] border border-[#546E7A]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
              placeholder="Search buyers..." />
            <div className="inline-flex bg-[#1A2744] border border-[#546E7A]/30 rounded-lg p-1 self-start md:self-auto">
              <button onClick={() => setViewMode('default')}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${viewMode === 'default' ? 'bg-[#243B55] text-white' : 'text-gray-400 hover:text-white'}`}>
                Cards
              </button>
              <button onClick={() => setViewMode('readiness')}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${viewMode === 'readiness' ? 'bg-[#243B55] text-white' : 'text-gray-400 hover:text-white'}`}>
                Readiness
              </button>
            </div>
          </div>
        )}

        {/* Buyer Cards */}
        {filtered.length === 0 ? (
          <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 text-center py-16">
            <p className="text-[#B0BEC5] text-lg mb-2">{search ? 'No buyers match your search' : 'No buyers yet'}</p>
            <p className="text-gray-500 mb-4">Add your first buyer to start evaluating properties.</p>
            {!search && (
              <button onClick={() => setShowCreate(true)}
                className="px-6 py-2 bg-[#00E676] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00C853] transition-colors">
                Add Your First Buyer
              </button>
            )}
          </div>
        ) : viewMode === 'readiness' ? (
          <div className="space-y-3">
            {filteredReadiness.map((r, i) => (
              <div key={r.buyer_id} className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-5">
                <div className="flex items-start gap-3 md:gap-4">
                  {/* Rank */}
                  <div className="text-[#00D4FF] font-bold text-lg shrink-0 w-7 text-center pt-1">#{i + 1}</div>

                  {/* Score Ring */}
                  <div
                    className="shrink-0 w-16 h-16 rounded-full flex items-center justify-center"
                    style={{ background: `conic-gradient(${READINESS_RING_COLOR[r.readiness_label] || '#6B7280'} ${r.readiness_score}%, #243B55 ${r.readiness_score}% 100%)` }}
                  >
                    <div className="w-12 h-12 rounded-full bg-[#1A2744] flex items-center justify-center">
                      <span className="text-white font-bold text-lg leading-none">{r.readiness_score}</span>
                    </div>
                  </div>

                  {/* Main */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/match-board?buyer=${r.buyer_id}`} className="text-white font-semibold hover:text-[#00D4FF] transition-colors truncate">
                        {r.buyer_name}
                      </Link>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium border ${READINESS_BADGE[r.readiness_label] || ''}`}>
                        {r.readiness_label}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {r.timeline && (
                        <span className="px-2 py-0.5 bg-[#0A0E27] border border-[#546E7A]/40 rounded text-xs text-gray-300">
                          {TIMELINE_LABELS[r.timeline] || r.timeline}
                        </span>
                      )}
                      {r.pre_approval_status && (
                        <span className="px-2 py-0.5 bg-[#0A0E27] border border-[#546E7A]/40 rounded text-xs text-gray-300">
                          {PREAPPROVAL_LABELS[r.pre_approval_status] || r.pre_approval_status}
                        </span>
                      )}
                      {r.motivation && (
                        <span className="px-2 py-0.5 bg-[#0A0E27] border border-[#546E7A]/40 rounded text-xs text-gray-300">
                          {MOTIVATION_LABELS[r.motivation] || r.motivation}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Right: top match + engagement (md+ only; mobile-stacked below) */}
                  <div className="hidden md:block text-right shrink-0 max-w-[220px]">
                    {r.top_property_address ? (
                      <div>
                        <div className="text-gray-500 text-xs">Top match</div>
                        <div className="text-white text-sm truncate">{r.top_property_address}</div>
                        {r.top_fit_score_pct !== null && (
                          <div className="text-[#00E676] font-bold text-sm">{r.top_fit_score_pct}%</div>
                        )}
                      </div>
                    ) : (
                      <div className="text-gray-600 text-xs">No matches yet</div>
                    )}
                    <div className="text-gray-500 text-xs mt-2">
                      {r.engagement_count} recent {r.engagement_count === 1 ? 'activity' : 'activities'}
                    </div>
                  </div>
                </div>

                {/* Mobile-only top match + engagement row */}
                <div className="md:hidden mt-3 pt-3 border-t border-[#546E7A]/20 flex justify-between gap-3 text-xs">
                  <div className="min-w-0 flex-1">
                    {r.top_property_address ? (
                      <span className="text-gray-400">
                        Top: <span className="text-white">{r.top_property_address}</span>
                        {r.top_fit_score_pct !== null && <span className="text-[#00E676] font-medium"> ({r.top_fit_score_pct}%)</span>}
                      </span>
                    ) : (
                      <span className="text-gray-600">No matches yet</span>
                    )}
                  </div>
                  <span className="text-gray-500 shrink-0">
                    {r.engagement_count} recent
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filtered.map(buyer => {
              const r = readinessByBuyer[buyer.id];
              return (
              <div key={buyer.id} className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-5">
                <div className="flex justify-between items-start mb-3 gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-semibold text-lg truncate">{buyer.name}
                      {buyer.criteria_pending && <span className="ml-2 text-[10px] align-middle text-yellow-400 border border-yellow-500/40 rounded px-1">criteria pending</span>}
                    </h3>
                    {buyer.email && <p className="text-gray-400 text-sm truncate">{buyer.email}</p>}
                    {buyer.phone && <p className="text-gray-500 text-sm truncate">{buyer.phone}</p>}
                    {buyerState(buyer.id) && (
                      <div className="mt-1.5">
                        <ScoringChip
                          state={buyerState(buyer.id)}
                          failedJob={failedJob('buyer', buyer.id)}
                          onRetry={retryScoring}
                        />
                      </div>
                    )}
                    {buyer.selling && (
                      <p className="text-xs mt-1 truncate">
                        <span className="text-gray-500">Also selling: </span>
                        <Link to={buyer.selling.is_draft ? `/listing-lab/${buyer.selling.property_id}` : `/properties/${buyer.selling.property_id}`}
                          className="text-[#00D4FF] hover:text-[#00E676]">{buyer.selling.address || 'their listing'} →</Link>
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {r && (
                      <span
                        title={`${r.readiness_label} (${r.readiness_score}/100)`}
                        className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white ${READINESS_DOT_BG[r.readiness_label] || 'bg-gray-500'}`}
                      >
                        {r.readiness_score}
                      </span>
                    )}
                    <button onClick={() => handleDelete(buyer.id, buyer.name)}
                      className="text-gray-600 hover:text-red-400 text-sm transition-colors" title="Delete buyer">✕</button>
                  </div>
                </div>
                {buyer.notes && (
                  <p className="text-gray-500 text-sm mb-3 line-clamp-2">{buyer.notes}</p>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Timeline</label>
                    <select value={buyer.timeline || 'exploring'}
                      onChange={(e) => updateUrgency(buyer.id, 'timeline', e.target.value)}
                      className="w-full px-2 py-1.5 bg-[#0A0E27] border border-[#546E7A]/50 rounded text-xs text-white focus:outline-none focus:ring-1 focus:ring-[#00D4FF]">
                      <option value="30_days">30 days</option>
                      <option value="60_days">60 days</option>
                      <option value="90_days">90 days</option>
                      <option value="6_months">6 months</option>
                      <option value="exploring">Exploring</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Pre-Approval</label>
                    <select value={buyer.pre_approval_status || 'unknown'}
                      onChange={(e) => updateUrgency(buyer.id, 'pre_approval_status', e.target.value)}
                      className="w-full px-2 py-1.5 bg-[#0A0E27] border border-[#546E7A]/50 rounded text-xs text-white focus:outline-none focus:ring-1 focus:ring-[#00D4FF]">
                      <option value="approved">Approved</option>
                      <option value="in_process">In Process</option>
                      <option value="not_started">Not Started</option>
                      <option value="unknown">Unknown</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Motivation</label>
                    <select value={buyer.motivation || 'casually_looking'}
                      onChange={(e) => updateUrgency(buyer.id, 'motivation', e.target.value)}
                      className="w-full px-2 py-1.5 bg-[#0A0E27] border border-[#546E7A]/50 rounded text-xs text-white focus:outline-none focus:ring-1 focus:ring-[#00D4FF]">
                      <option value="must_move">Must Move</option>
                      <option value="want_to_move">Want to Move</option>
                      <option value="casually_looking">Casual</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">

                  <Link to={`/match-board?buyer=${buyer.id}`}
                    className="px-3 py-1.5 bg-[#243B55] text-[#00E676] text-sm font-medium rounded-lg hover:bg-[#2d4a6a] transition-colors">
                    Match Board
                  </Link>
                  <Link to={`/buyers/${buyer.id}/criteria`}
                    className="px-3 py-1.5 bg-[#243B55] text-[#00D4FF] text-sm font-medium rounded-lg hover:bg-[#2d4a6a] transition-colors">
                    Criteria
                  </Link>
                  <button onClick={() => setResetId(resetId === buyer.id ? null : buyer.id)}
                    className="px-3 py-1.5 bg-[#243B55] text-gray-400 text-sm font-medium rounded-lg hover:bg-[#2d4a6a] hover:text-white transition-colors">
                    Reset Password
                  </button>
                </div>
                {resetId === buyer.id && (
                  <div className="mt-3 flex gap-2 items-center flex-wrap">
                    <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                      className="flex-1 min-w-0 px-3 py-1.5 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#00D4FF]"
                      placeholder="New password (min 8 chars)" />
                    <button onClick={() => handleResetPassword(buyer.id)}
                      className="px-3 py-1.5 bg-[#00E676] text-[#0A0E27] text-sm font-semibold rounded-lg hover:bg-[#00C853]">
                      Save
                    </button>
                    <button onClick={() => { setResetId(null); setNewPassword(''); }}
                      className="px-3 py-1.5 text-gray-500 text-sm hover:text-white">
                      Cancel
                    </button>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
